import { useFormikContext } from 'formik';
import Spacer from '@antimatterhq/ui/components/spacer';
import { Label } from '@antimatterhq/ui/components/label';
import { Link } from '@antimatterhq/ui/components/typography';
import {
  AddDetectionRuleSchemaValues,
  Fidelity,
} from '@/zod/schemas/detectionRules/AddDetectionRule';
import {
  annotationsRlTooltip,
  severityTooltip,
} from '@/strings/StringsTooltips';
import {
  FormDropdown,
  FormError,
  FormInput,
  FormTextarea,
  FormTypeahead,
} from '@/components/form/form';
import { useGetWorkspaceConfig } from '@/hooks/workspace/useGetWorkspaceConfig';
import { useParentUrl } from '@/hooks/iframe/useParentUrl';
import { FormMultiValues } from '@/components/form/multiValues/FormMultiValues';

export type RuleMetadataSectionPropsType = {
  isInEditMode?: boolean;
};

const SEVERITIES = ['Informational', 'Medium', 'High', 'Critical'];

export const RuleMetadataSection = ({
  isInEditMode,
}: RuleMetadataSectionPropsType) => {
  const { values, handleChange, handleBlur } =
    useFormikContext<AddDetectionRuleSchemaValues>();
  const { data: config } = useGetWorkspaceConfig();
  const parentUrl = useParentUrl();

  return (
    <>
      <Label htmlFor="name" id="name-label" required>
        Rule name
      </Label>
      <FormInput
        type="text"
        name="name"
        value={values.name}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder="Add a rule name"
        disabled={isInEditMode}
        aria-labelledby="name-label"
      />
      <FormError name="name" />

      <Spacer size="lg" />

      <Label htmlFor="comment" id="comment-label">
        Rule comment
      </Label>
      <FormInput
        type="text"
        name="comment"
        value={values.comment}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder="Add a comment for the rule"
        aria-labelledby="comment-label"
      />
      <FormError name="comment" />

      <Spacer size="lg" />

      <Label
        htmlFor="severity"
        id="severity-label"
        required
        info={severityTooltip}
      >
        Severity
      </Label>
      <FormTypeahead
        value={values.severity}
        onChange={handleChange}
        onBlur={handleBlur}
        name="severity"
        placeholder="Enter a severity"
        aria-labelledby="severity-label"
        items={SEVERITIES}
        minChars={0}
        filterResults={false}
      />
      <FormError name="severity" />

      <Spacer size="lg" />

      <Label htmlFor="category" id="category-label" required>
        Category
      </Label>
      <FormDropdown
        options={config?.spec.detectionRuleMetadata?.detectionCategories || []}
        value={values.category}
        onChange={handleChange}
        onBlur={handleBlur}
        name="category"
        placeholder="Choose a category"
        aria-labelledby="category-label"
        emptyState={
          <>
            Categories can be added via the{' '}
            <Link to={`${parentUrl}/configure/workspace`} target="_blank">
              workspace config
            </Link>
          </>
        }
      />
      <FormError name="category" />

      <Spacer size="lg" />

      <Label htmlFor="fidelity" id="fidelity-label" required>
        Fidelity
      </Label>
      <FormDropdown
        options={[...Fidelity]}
        value={values.fidelity}
        onChange={handleChange}
        onBlur={handleBlur}
        name="fidelity"
        placeholder="Choose fidelity"
        aria-labelledby="fidelity-label"
      />
      <FormError name="fidelity" />

      <Spacer size="lg" />

      <Label htmlFor="objective" id="objective-label">
        Objective
      </Label>
      <FormTextarea
        value={values.objective}
        onChange={handleChange}
        onBlur={handleBlur}
        name="objective"
        placeholder="Describe the objective of the rule"
        aria-labelledby="objective-label"
      />
      <FormError name="objective" />

      <Spacer size="lg" />

      <Label info={annotationsRlTooltip}>Annotations (optional)</Label>
      <FormMultiValues
        name="annotations"
        onBlur={handleBlur}
        onChange={handleChange}
        values={values.annotations}
        defaultValue={{ key: '', value: '' }}
      />
    </>
  );
};
